import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import { PROJECTS } from '../data';

const ALL_CATS = ['All', ...new Set(PROJECTS.map(p => p.category))];

function Card({ p, i }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.div
      initial={{opacity:0,y:36}} whileInView={{opacity:1,y:0}}
      transition={{duration:0.55,delay:(i%3)*0.1}} viewport={{once:true,margin:'-40px'}}
      whileHover={{y:-8}}
      onHoverStart={()=>setHov(true)} onHoverEnd={()=>setHov(false)}
      className="glass border border-white/[0.06] rounded-3xl overflow-hidden card-lift cursor-default"
    >
      {/* Thumbnail */}
      <div className={`relative h-44 bg-gradient-to-br ${p.gradient} overflow-hidden`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span animate={{scale:hov?1.2:1,rotate:hov?8:0}} transition={{duration:0.3}}
            className="text-7xl opacity-25">{p.icon}</motion.span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d1a] via-transparent to-transparent" />

        {/* Category badge */}
        <div className="absolute top-4 left-4">
          <span className="text-xs font-bold px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white border border-white/10">
            {p.category}
          </span>
        </div>
        {p.featured && (
          <div className="absolute top-4 right-4">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-violet-600/80 text-white">★ Featured</span>
          </div>
        )}

        {/* Hover links */}
        <AnimatePresence>
          {hov && (
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center gap-4"
            >
              {[
                {icon:<FaExternalLinkAlt size={13}/>, href:p.demo  ?? '#', label:'Live Demo'},
                {icon:<FaGithub size={15}/>,          href:p.github ?? '#', label:'GitHub'  },
              ].map(btn => (
                <motion.a key={btn.label} href={btn.href} target="_blank" rel="noopener noreferrer"
                  initial={{scale:0}} animate={{scale:1}} transition={{delay:0.04}}
                  className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-violet-600 transition-all duration-200"
                  title={btn.label}
                >{btn.icon}</motion.a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Body */}
      <div className="p-6">
        <h3 className="text-white font-bold text-xl mb-2 group-hover:text-violet-300 transition-colors">{p.title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed mb-4">{p.desc}</p>

        {/* Feature chips */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {p.features.slice(0,3).map(f => (
            <span key={f} className="text-xs px-2 py-1 rounded-lg"
              style={{backgroundColor:p.accent+'12',color:p.accent+'bb',border:`1px solid ${p.accent}20`}}>
              {f}
            </span>
          ))}
          {p.features.length > 3 && (
            <span className="text-xs px-2 py-1 rounded-lg bg-white/[0.05] text-slate-500">
              +{p.features.length-3} more
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between py-3 border-t border-white/[0.05] mb-4">
          {Object.entries(p.stats).map(([k,v]) => (
            <div key={k} className="text-center">
              <p className="text-white text-sm font-bold">{v}</p>
              <p className="text-slate-600 text-xs capitalize">{k}</p>
            </div>
          ))}
        </div>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-1.5">
          {p.tech.map(t => (
            <span key={t} className="text-xs px-2.5 py-1 rounded-full glass border border-white/[0.06] text-slate-500 font-mono">{t}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? PROJECTS : PROJECTS.filter(p => p.category === filter);

  return (
    <section id="projects" className="section-pad relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/8 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div initial={{opacity:0,y:28}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.55}} viewport={{once:true}} className="text-center mb-16">
          <p className="text-violet-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">— Featured Work</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Projects That <span className="grad-text">Ship</span>
          </h2>
          <p className="text-slate-400 max-w-md mx-auto">Enterprise systems powering real businesses across Ethiopia and beyond</p>
        </motion.div>

        {/* Filter tabs */}
        <motion.div initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.45}} viewport={{once:true}}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {ALL_CATS.map(cat => (
            <motion.button key={cat} onClick={()=>setFilter(cat)}
              whileHover={{scale:1.05}} whileTap={{scale:0.95}}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                filter === cat
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/25'
                  : 'glass text-slate-400 hover:text-white border border-white/[0.06] hover:border-violet-500/20'
              }`}
            >{cat}</motion.button>
          ))}
        </motion.div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div key={filter}
            initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} transition={{duration:0.25}}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filtered.map((p,i) => <Card key={p.id} p={p} i={i} />)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
